import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SuperadminLayout, { DarkCard } from "./SuperadminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Key, ShieldCheck, DownloadSimple, Copy, Warning, WarningCircle } from "@phosphor-icons/react";

export default function SuperadminSecurity() {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [notActivated, setNotActivated] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");

  const load = async () => {
    setNotActivated(false); setLoadErr(null);
    try {
      const { data } = await api.get("/auth/totp/backup-codes");
      setStatus(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "";
      if (e?.response?.status === 400 && msg.includes("pas activé")) {
        setNotActivated(true);
      } else {
        setLoadErr(msg || "Chargement impossible");
      }
    }
  };
  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/totp/backup-codes/regenerate", { code: code.trim() });
      setDialog({ fresh: data.backup_codes });
      setCode("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Régénération impossible");
    } finally { setBusy(false); }
  };

  const relogForTotp = async () => {
    try { await logout(); } catch { /* ignore */ }
    window.location.href = "/login";
  };

  const download = (codes) => {
    const content =
      `Codes de secours 2FA — Questiomed Backoffice\n` +
      `Compte : ${user?.email}\n` +
      `Généré le : ${new Date().toLocaleString("fr-FR")}\n\n` +
      `IMPORTANT : Chaque code est à usage unique.\n\n` +
      codes.map((c, i) => `  ${String(i + 1).padStart(2, "0")}.  ${c}`).join("\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `questiomed-backup-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Codes téléchargés");
  };

  const copy = async (codes) => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success("Codes copiés");
    } catch { toast.error("Copie impossible"); }
  };

  return (
    <SuperadminLayout title="Sécurité 2FA">
      <div className="max-w-2xl">
        {notActivated ? (
          <DarkCard className="p-6" data-testid="totp-not-activated">
            <div className="flex items-start gap-3 mb-4">
              <WarningCircle size={24} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
              <div>
                <h2 className="text-white font-semibold">Le 2FA n'est pas encore activé sur votre compte</h2>
                <p className="text-sm text-[#8494A8] mt-1 leading-relaxed">
                  Votre compte est superadmin mais le 2FA n'a pas encore été configuré (probablement à cause d'une réinitialisation).
                  Déconnectez-vous puis reconnectez-vous : l'écran de configuration TOTP s'affichera automatiquement et vos codes de secours seront générés.
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <Button onClick={relogForTotp} className="bg-[#E4572E] hover:bg-[#F27551] text-white" data-testid="totp-relogin">
                Se reconnecter pour configurer le 2FA
              </Button>
              <Button variant="ghost" onClick={load} className="text-[#B7C4D6] hover:bg-white/10 hover:text-white">
                Rafraîchir le statut
              </Button>
            </div>
          </DarkCard>
        ) : loadErr ? (
          <DarkCard className="p-6" data-testid="totp-load-error">
            <p className="text-red-400">{loadErr}</p>
          </DarkCard>
        ) : (
          <>
            <DarkCard className="p-6 mb-6" data-testid="totp-security-card">
              <div className="flex items-start gap-3 mb-4">
                <ShieldCheck size={22} weight="fill" className="text-[#3A8FA5] mt-0.5" />
                <div>
                  <h2 className="text-white font-semibold">Authentification à deux facteurs</h2>
                  <p className="text-xs text-[#8494A8] mt-0.5">Statut de vos codes de secours</p>
                </div>
              </div>
              {status ? (
                <div className="grid grid-cols-3 gap-3 mb-2">
                  <StatTile label="Total" value={status.total} />
                  <StatTile label="Restants" value={status.remaining} color="#B0C97F" />
                  <StatTile label="Utilisés" value={status.used} color="#E4A72E" />
                </div>
              ) : (
                <p className="text-sm text-[#8494A8]">Chargement…</p>
              )}
              {status && status.remaining <= 3 && (
                <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/40 rounded-xl flex items-start gap-2">
                  <Warning size={16} className="text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-[#F0BAB4]">
                    Vous n'avez plus que <strong>{status.remaining} codes</strong> — pensez à en régénérer avant d'être bloqué.
                  </p>
                </div>
              )}
            </DarkCard>

            <DarkCard className="p-6" data-testid="regen-card">
              <div className="flex items-start gap-3 mb-4">
                <Key size={22} weight="fill" className="text-[#E4572E] mt-0.5" />
                <div>
                  <h2 className="text-white font-semibold">Régénérer les codes de secours</h2>
                  <p className="text-xs text-[#8494A8] mt-0.5">
                    Invalide tous les anciens codes et génère 10 nouveaux. Nécessite un code TOTP valide de votre application authenticator.
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Code TOTP actuel (6 chiffres)</Label>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9 ]/g, "").slice(0, 8))}
                    placeholder="123 456"
                    className="mt-2 bg-[#081221] border-white/10 text-white font-mono text-center text-xl tracking-widest placeholder:text-[#5F758F]"
                    data-testid="regen-code"
                  />
                </div>
                <Button
                  onClick={regenerate} disabled={busy || code.replace(/\s/g, "").length < 6}
                  className="w-full bg-[#E4572E] hover:bg-[#F27551] text-white disabled:opacity-40"
                  data-testid="regen-submit"
                >
                  {busy ? "Régénération…" : "Régénérer 10 nouveaux codes"}
                </Button>
              </div>
            </DarkCard>
          </>
        )}

        {dialog?.fresh && (
          <Dialog open onOpenChange={(o) => !o && setDialog(null)}>
            <DialogContent className="bg-[#0F2136] border-white/10 text-white max-w-lg" data-testid="fresh-codes-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Key size={22} weight="fill" className="text-[#E4572E]" />
                  Nouveaux codes de secours
                </DialogTitle>
              </DialogHeader>
              <div className="p-3 bg-red-500/10 border border-red-500/40 rounded-xl text-xs text-[#F0BAB4] flex items-start gap-2">
                <Warning size={14} className="text-red-400 shrink-0 mt-0.5" />
                <span>Anciens codes invalidés. Sauvegardez ces 10 codes MAINTENANT — cette page ne sera plus affichée.</span>
              </div>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-[#081221] rounded-xl p-4 border border-white/10">
                {dialog.fresh.map((c, i) => (
                  <div key={c} className="flex items-center gap-2 py-1">
                    <span className="text-[#5F758F] text-xs w-5">{String(i + 1).padStart(2, "0")}.</span>
                    <code className="text-[#B0C97F] tracking-wider">{c}</code>
                  </div>
                ))}
              </div>
              <DialogFooter className="flex gap-2 flex-row sm:justify-between">
                <div className="flex gap-2">
                  <Button onClick={() => download(dialog.fresh)} className="bg-[#E4572E] hover:bg-[#F27551]" data-testid="fresh-codes-download">
                    <DownloadSimple size={14} className="mr-1" /> .txt
                  </Button>
                  <Button onClick={() => copy(dialog.fresh)} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="fresh-codes-copy">
                    <Copy size={14} className="mr-1" /> Copier
                  </Button>
                </div>
                <Button onClick={() => setDialog(null)} className="bg-[#3A8FA5] hover:bg-[#164E5F]" data-testid="fresh-codes-close">
                  J'ai sauvegardé
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </SuperadminLayout>
  );
}

function StatTile({ label, value, color = "#B7C4D6" }) {
  return (
    <div className="bg-[#081221] rounded-xl p-3 border border-white/10">
      <p className="text-[10px] uppercase tracking-widest text-[#8494A8]">{label}</p>
      <p className="text-2xl font-bold mt-1 font-mono" style={{ color }}>{value}</p>
    </div>
  );
}
